/*
  SOLVERA in-house wishlist (replaces the Wishlist Hero app).

  Storage: localStorage 'solvera:wishlist:v1' - array of { handle, id }.
  Anonymous-first (like the app's guest mode). Customer-account sync needs a
  backend (app proxy) and is deliberately out of scope - see docs/apps.md.

  Elements:
    <wishlist-button data-product-handle data-product-id> - toggle heart
    <wishlist-count>  - header badge, hidden at zero
    <wishlist-grid>   - wishlist page; fetches each saved product rendered
                        through templates/product.wishlist-card.liquid
                        (?view=wishlist-card, layout none) so cards are
                        pixel-identical to collection cards.

  State sync is event-driven: every change dispatches 'wishlist:change' on
  document; buttons/counters/grids anywhere on the page stay consistent.
*/

(() => {
  const KEY = 'solvera:wishlist:v1';
  const LEGACY_KEY = 'wishlisthero_.wl-itms';

  // Only well-formed Shopify handles pass: values later end up in fetch URLs
  // and the rendered response is injected into the wishlist grid.
  const VALID_HANDLE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

  const readList = () => {
    try {
      const list = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Array.isArray(list)
        ? list.filter((x) => x && typeof x.handle === 'string' && VALID_HANDLE.test(x.handle))
        : [];
    } catch (error) {
      return [];
    }
  };

  const writeList = (list) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(list));
    } catch (error) {
      // Storage full/blocked (private mode) - wishlist degrades gracefully.
    }
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { list } }));
  };

  // One-time migration of Wishlist Hero guest data so nobody loses their list
  // when the app is uninstalled. The app stores an array of product ids or
  // objects; we keep whatever has enough shape to resolve.
  const migrateLegacy = () => {
    if (localStorage.getItem(KEY) !== null) return;
    try {
      const legacy = JSON.parse(localStorage.getItem(LEGACY_KEY) || '[]');
      if (Array.isArray(legacy) && legacy.length) {
        const mapped = legacy
          .map((item) => {
            if (item && typeof item === 'object') {
              const handle = item.handle || item.productHandle;
              const id = item.id || item.productId || item.product_id;
              return handle ? { handle: String(handle), id: id ? String(id) : null } : null;
            }
            return null;
          })
          .filter(Boolean);
        if (mapped.length) localStorage.setItem(KEY, JSON.stringify(mapped));
      }
    } catch (error) {
      /* ignore malformed legacy data */
    }
  };
  migrateLegacy();

  // Cross-tab synchronization: localStorage 'storage' events fire in every
  // OTHER tab; re-broadcast as our internal event so all components resync.
  window.addEventListener('storage', (event) => {
    if (event.key === KEY) {
      document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { list: readList() } }));
    }
  });

  const wishlist = {
    list: readList,
    has: (handle) => readList().some((x) => x.handle === handle),
    toggle(handle, id) {
      const list = readList();
      const idx = list.findIndex((x) => x.handle === handle);
      let added;
      if (idx >= 0) {
        list.splice(idx, 1);
        added = false;
      } else {
        list.push({ handle, id: id || null });
        added = true;
      }
      writeList(list);
      return added;
    },
    remove(handle) {
      writeList(readList().filter((x) => x.handle !== handle));
    },
    // Extension point for customer-account sync. When a backend endpoint
    // (app proxy) exists, call SolveraWishlist.mergeRemote(items) with the
    // customer's server-side list after login: local and remote merge by
    // handle, and the merged list becomes the new local truth. Theme JS
    // cannot write customer metafields directly, so pushing the merged list
    // back to the server stays the endpoint's job.
    mergeRemote(remoteItems) {
      const merged = readList();
      (Array.isArray(remoteItems) ? remoteItems : []).forEach((item) => {
        if (
          item &&
          typeof item.handle === 'string' &&
          VALID_HANDLE.test(item.handle) &&
          !merged.some((x) => x.handle === item.handle)
        ) {
          merged.push({ handle: item.handle, id: item.id ? String(item.id) : null });
        }
      });
      writeList(merged);
      return merged;
    },
  };
  window.SolveraWishlist = wishlist;

  let toastTimer;
  const toast = (message) => {
    let el = document.getElementById('wishlist-toast');
    if (!el) {
      el = document.createElement('div');
      el.id = 'wishlist-toast';
      el.className = 'wishlist-toast';
      el.setAttribute('role', 'status');
      document.body.appendChild(el);
    }
    el.textContent = message;
    el.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-visible'), 2400);
  };

  if (!customElements.get('wishlist-button')) {
    customElements.define(
      'wishlist-button',
      class WishlistButton extends HTMLElement {
        connectedCallback() {
          this.handle = this.dataset.productHandle;
          this.button = this.querySelector('button');
          if (!this.button || !this.handle) return;
          // Stable bound refs: third-party scripts (e.g. Wishlist Hero during
          // the transition period) move card nodes, re-firing this callback.
          // addEventListener dedupes identical (type, fn) pairs, so reusing
          // the same bound function prevents double-toggle bugs.
          this.boundClick = this.boundClick || this.onClick.bind(this);
          this.onChange = this.onChange || this.sync.bind(this);
          this.button.addEventListener('click', this.boundClick);
          document.addEventListener('wishlist:change', this.onChange);
          this.sync();
        }

        disconnectedCallback() {
          document.removeEventListener('wishlist:change', this.onChange);
        }

        onClick(event) {
          event.preventDefault();
          const added = wishlist.toggle(this.handle, this.dataset.productId);
          if (added) {
            this.classList.add('just-added');
            this.addEventListener('animationend', () => this.classList.remove('just-added'), { once: true });
          }
          toast(added ? this.dataset.textAdded : this.dataset.textRemoved);
        }

        sync() {
          const saved = wishlist.has(this.handle);
          this.classList.toggle('is-saved', saved);
          this.button.setAttribute('aria-pressed', saved ? 'true' : 'false');
        }
      }
    );
  }

  if (!customElements.get('wishlist-count')) {
    customElements.define(
      'wishlist-count',
      class WishlistCount extends HTMLElement {
        connectedCallback() {
          this.onChange = this.onChange || this.sync.bind(this);
          document.addEventListener('wishlist:change', this.onChange);
          this.sync();
        }

        disconnectedCallback() {
          document.removeEventListener('wishlist:change', this.onChange);
        }

        sync() {
          const n = wishlist.list().length;
          const next = n > 0 ? String(n) : '';
          if (next === this.textContent) return;
          // Dawn's .cart-count-bubble:empty handles visibility at zero.
          this.textContent = next;
          if (next !== '') {
            this.classList.remove('count-pop');
            void this.offsetWidth; // restart the animation
            this.classList.add('count-pop');
            this.addEventListener('animationend', () => this.classList.remove('count-pop'), { once: true });
          }
        }
      }
    );
  }

  if (!customElements.get('wishlist-grid')) {
    customElements.define(
      'wishlist-grid',
      class WishlistGrid extends HTMLElement {
        connectedCallback() {
          this.grid = this.querySelector('[data-wishlist-items]');
          this.emptyState = this.querySelector('[data-wishlist-empty]');
          this.onChange = this.onChange || this.render.bind(this);
          document.addEventListener('wishlist:change', this.onChange);
          this.render();
        }

        disconnectedCallback() {
          document.removeEventListener('wishlist:change', this.onChange);
        }

        async render() {
          const list = wishlist.list();
          this.emptyState.hidden = list.length > 0;
          this.grid.hidden = list.length === 0;
          if (list.length === 0) {
            this.grid.innerHTML = '';
            return;
          }
          // Render each saved product through the wishlist-card view so the
          // markup is exactly Dawn's card-product. 404s (deleted/unpublished
          // products) are pruned from the list.
          const results = await Promise.all(
            list.map(async ({ handle }) => {
              try {
                const res = await fetch(
                  `${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(handle)}?view=wishlist-card`
                );
                if (res.status === 404) return { handle, gone: true };
                if (!res.ok) return { handle, html: null };
                return { handle, html: await res.text() };
              } catch (error) {
                return { handle, html: null };
              }
            })
          );
          results.filter((r) => r.gone).forEach((r) => wishlist.remove(r.handle));
          const items = results.filter((r) => r.html);
          this.grid.innerHTML = items
            .map(
              (r) =>
                `<li class="grid__item">${r.html}</li>`
            )
            .join('');
        }
      }
    );
  }
})();
